import type { TranslatableValue } from "@@/shared/translations/translation";
import { translation } from "@@/shared/translations/translation";
import { z } from "zod";

// Applies per...
const accommodationRateOptionApplies = [
    "PER_NIGHT",
    "PER_STAY",
    "PER_NIGHTS",
] as const;
const accommodationRateOptionAppliesZodEnum2 = z.enum(
    accommodationRateOptionApplies,
);
//Should apply every x nights
const accommodationRateOptionAppliesFixDays = z.object({
    type: z.literal("PER_NIGHTS"),
    nights: z.number().min(1),
});
//Should apply every night
const accommodationRateOptionAppliesPerNight = z.object({
    type: z.literal("PER_NIGHT"),
});
//Should apply only once per stay
const accommodationRateOptionAppliesPerStay = z.object({
    type: z.literal("PER_STAY"),
});

const AccommodationRateOptionAppliesZodEnumV2 = z.discriminatedUnion("type", [
    accommodationRateOptionAppliesFixDays,
    accommodationRateOptionAppliesPerNight,
    accommodationRateOptionAppliesPerStay,
]);

const accommodationRateOptionAppliesZodEnum =
    AccommodationRateOptionAppliesZodEnumV2.or(
        accommodationRateOptionAppliesZodEnum2,
    );

// Extract the type for use in your meta object
type AccommodationRateOptionApplies = z.infer<
    typeof accommodationRateOptionAppliesZodEnum
>;

type AccommodationRateOptionAppliesV2 = z.infer<
    typeof AccommodationRateOptionAppliesZodEnumV2
>;

// Extract just the type literal values for use as keys
type AccommodationRateOptionType = AccommodationRateOptionAppliesV2["type"];

const accommodationRateOptionAppliesMeta = {
    PER_NIGHT: {
        iconName: "fa-moon",
        title: translation({ sv: "Per natt", en: "night" }),
    },
    PER_STAY: {
        iconName: "fa-suitcase",
        title: translation({ sv: "Per vistelse", en: "stay" }),
    },
    PER_NIGHTS: {
        iconName: "fa-moon",
        title: translation({ sv: "Antal nätter", en: "night" }),
    },
};

//Helper function to get the type of applies for use in applicaiton
//Should be removed once all is migrated to the new zod schema
const getAccommodationRateOptionType = (
    applies: AccommodationRateOptionApplies,
) => {
    if (typeof applies === "string") return applies;

    return applies.type;
};
// Check if applies is PER_NIGHTS guard
const isAppliesPerNights = (
    applies: AccommodationRateOptionApplies,
): applies is { type: "PER_NIGHTS"; nights: number } => {
    if (typeof applies === "string") return false;

    return applies.type === "PER_NIGHTS";
};

// Applies To
const accommodationRateOptionAppliesTo = [
    "GUEST",
    "ROOM",
    "ADULT",
    "CHILD",
    "INFANT",
] as const;

const accommodationRateOptionAppliesToZodEnum = z.enum(
    accommodationRateOptionAppliesTo,
);
type AccommodationRateOptionAppliesTo = z.infer<
    typeof accommodationRateOptionAppliesToZodEnum
>;
const accommodationRateOptionAppliesToMeta: {
    [key in AccommodationRateOptionAppliesTo]: {
        iconName?: `fa-${string}`;
        title: TranslatableValue;
    };
} = {
    GUEST: {
        iconName: "fa-family",
        title: translation({ sv: "Gäst", en: "Guest" }),
    },
    ROOM: {
        iconName: "fa-door-closed",
        title: translation({ sv: "Boendeenhet", en: "Room" }),
    },
    ADULT: {
        iconName: "fa-person",
        title: translation({ sv: "Vuxen", en: "Adult" }),
    },
    CHILD: {
        iconName: "fa-child",
        title: translation({ sv: "Barn", en: "Child" }),
    },
    INFANT: {
        iconName: "fa-baby-carriage",
        title: translation({ sv: "Spädbarn", en: "Infant" }),
    },
};

export {
    accommodationRateOptionApplies,
    accommodationRateOptionAppliesMeta,
    accommodationRateOptionAppliesTo,
    accommodationRateOptionAppliesToMeta,
    accommodationRateOptionAppliesToZodEnum,
    accommodationRateOptionAppliesZodEnum,
    getAccommodationRateOptionType,
    isAppliesPerNights,
};
export type {
    AccommodationRateOptionApplies,
    AccommodationRateOptionAppliesTo,
    AccommodationRateOptionAppliesV2,
    AccommodationRateOptionType,
};
